import { useState, useCallback } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import type {
  UpdatePartnerPreRegistrationRequest,
  UpdatePartnerPreRegistrationApiResponse,
  UpdatePartnerPreRegistrationErrorResponse,
  UseUpdatePartnerPreRegistrationReturn,
  CheckListStatus,
  PartnerPreRegistrationStatus,
} from "../types"

/**
 * パートナー事前登録の更新処理を行うカスタムフック
 *
 * @returns パートナー事前登録の更新処理関数とその状態
 */
export const useUpdatePartnerPreRegistration = (): UseUpdatePartnerPreRegistrationReturn => {
  const { patchWithToken } = useSmoothApi()
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * エラー状態をリセットする関数
   */
  const reset = useCallback(() => {
    setError(null)
  }, [])

  /**
   * パートナー事前登録を更新する関数
   *
   * @param id - 更新するパートナー事前登録のID
   * @param data - 更新データ
   * @param nextStatus - 次のステータス
   * @returns APIレスポンス
   * @throws 更新に失敗した場合はエラーをthrow
   */
  const updatePartnerPreRegistration = useCallback(
    async (
      id: number,
      data: UpdatePartnerPreRegistrationRequest,
      nextStatus: PartnerPreRegistrationStatus,
      checklistStatuses: CheckListStatus[] | undefined
    ): Promise<UpdatePartnerPreRegistrationApiResponse> => {
      if (!id) {
        throw new Error("IDが指定されていません")
      }

      setIsUpdating(true)
      setError(null)

      try {
        const url = `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/partner_pre_registrations/${id}`
        const payload = { ...data, status: nextStatus }
        if (checklistStatuses) {
          payload.checkedTypes = checklistStatuses
            .filter((item) => item.checked)
            .map((item) => item.id)
        }
        const response = await patchWithToken(url, payload)

        // 成功レスポンスの型チェック
        if (response.data && response.data.partnerPreRegistration) {
          return response.data as UpdatePartnerPreRegistrationApiResponse
        } else {
          throw new Error("レスポンスの形式が正しくありません")
        }
      } catch (error: any) {
        // エラーログ出力（開発環境での診断用）
        console.error("パートナー事前登録の更新に失敗しました:", error)

        // APIエラーレスポンスの処理
        if (error.response?.data) {
          const errorData = error.response.data as UpdatePartnerPreRegistrationErrorResponse
          const errorMessage =
            errorData.message || errorData.error || "システムエラーが発生しました"
          const apiError = new Error(errorMessage)
          ;(apiError as any).response = error.response
          setError(apiError)
          throw apiError
        } else {
          // ネットワークエラーなどの場合
          const networkError = new Error("通信エラーが発生しました")
          setError(networkError)
          throw networkError
        }
      } finally {
        setIsUpdating(false)
      }
    },
    [patchWithToken]
  )

  return {
    updatePartnerPreRegistration,
    isUpdating,
    error,
    reset,
  }
}
